import { useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ParentAppContext } from './ParentAppContext';
import { erpBase, erpPath } from '@/utils/erp-host';
import {
  LayoutDashboard, Users, GraduationCap, MessageSquare,
  Bell, Clock, Wallet, Settings, Calendar, FileText, AlertTriangle,
  History, ChevronLeft, ChevronRight, LogOut, ShieldCheck, NotebookPen,
  ClipboardList, CalendarRange, PenSquare, FileText as FileTextIcon, Upload,
} from 'lucide-react';

// `perm` is the pa_permissions module that gates this page's data on the
// backend — verified against pa-curriculum.routes.js, pa-fees.routes.js,
// pa-students.routes.js, pa-rbac-admin.routes.js, pa-assessment.routes.js.
// 'settings' has no dedicated module; mapped to 'classes' (its Academic
// Years section's actual gate). A missing `perm` means the route has no
// permission guard on the backend — leave unfiltered rather than hide it
// based on a permission no role has been granted.
const NAV_SECTIONS = [
  {
    title: 'Daily',
    items: [
      { path: 'dashboard',     label: 'Dashboard',        desc: 'Overview & quick actions',   icon: LayoutDashboard, cat: 'cat-blue',   perm: 'dashboard'     },
      { path: 'notifications', label: 'Communication Hub', desc: 'Push to parents',            icon: Bell,            cat: 'cat-amber',  perm: 'communication' },
      { path: 'class-feed',    label: 'Class Feed',        desc: 'Monitor parent feeds',       icon: MessageSquare,   cat: 'cat-teal',   perm: 'communication' },
      { path: 'diary',         label: 'Diary',             desc: 'Daily class notes',          icon: NotebookPen,     cat: 'cat-peach',  perm: 'diary'         },
      { path: 'help-desk',     label: 'Help Desk',         desc: 'Track parent issues',        icon: AlertTriangle,   cat: 'cat-rose',   perm: 'helpdesk'      },
    ],
  },
  {
    title: 'Regular',
    items: [
      { path: 'students',      label: 'Students',          desc: 'Enrolments & parents',       icon: Users,           cat: 'cat-green',  perm: 'students'  },
      { path: 'timetable',     label: 'Timetable',         desc: 'Class schedules',            icon: Clock,           cat: 'cat-rose',   perm: 'timetable' },
      { path: 'events',        label: 'Events Calendar',   desc: 'Holidays, exams, events',    icon: Calendar,        cat: 'cat-rose',   perm: 'events'    },
      { path: 'exams',         label: 'Exams',             desc: 'Terms, exams & marks',       icon: CalendarRange,   cat: 'cat-amber',  perm: 'exams'     },
      { path: 'marks',         label: 'Marks Entry',       desc: 'Enter & publish results',    icon: PenSquare,       cat: 'cat-amber',  perm: 'marks'     },
      { path: 'report-cards',  label: 'Report Cards',      desc: 'View student report cards',  icon: FileTextIcon,    cat: 'cat-amber',  perm: 'marks'     },
      { path: 'leave',         label: 'Leave Requests',    desc: 'Approve parent leaves',      icon: FileText,        cat: 'cat-peach',  perm: 'leave'     },
      { path: 'fees',          label: 'Fees',              desc: 'Payments & structures',      icon: Wallet,          cat: 'cat-green',  perm: 'fees'      },
      { path: 'teachers',      label: 'Teachers',          desc: 'Manage staff',               icon: Users,           cat: 'cat-purple', perm: 'users'     },
    ],
  },
  {
    title: 'Setup & Admin',
    items: [
      { path: 'curriculum',    label: 'Academic Setup',    desc: 'Classes & curriculum',       icon: GraduationCap,   cat: 'cat-indigo', perm: 'classes' },
      // Skills/grade-scale CRUD is gated on 'exams' (see pa-assessment.routes.js), not 'classes'.
      { path: 'assessment-scheme', label: 'Report Setup',  desc: 'Skills & grade scale',        icon: ClipboardList,   cat: 'cat-teal',   perm: 'exams'   },
      { path: 'users-roles',   label: 'Users & Roles',     desc: 'Staff access & permissions',  icon: ShieldCheck,     cat: 'cat-purple', perm: 'users'   },
      // /admin/change-logs is now gated on users.view (same as Users & Roles) —
      // it spans every module (fees, medical records, other classes'
      // students, ...), so it's restricted to unrestricted staff only.
      { path: 'change-logs',   label: 'Activity Log',      desc: 'Who changed what and when',   icon: History,         cat: 'cat-indigo', perm: 'users'   },
      // Gated on students.import (not the usual .view) — only Principal/
      // Coordinator hold it, so Class/Subject Teacher (who have students.view
      // but not import) correctly don't see this in the sidebar even though
      // they can see Students itself.
      { path: 'bulk-import',   label: 'Bulk Import',       desc: 'Import students & fees from CSV', icon: Upload,      cat: 'cat-green',  perm: 'students', action: 'import' },
      { path: 'settings',      label: 'Settings',          desc: 'School configuration',       icon: Settings,        cat: 'cat-blue',   perm: 'classes' },
    ],
  },
];

// null permissions = unrestricted (super_admin/bootstrap, mirrors the
// backend's hasPermission bypass in pa-rbac.middleware.js); undefined means
// "no permission data available" (crm_user fallback path below), which keeps
// today's behavior of showing every nav item.
function getUser(): { name: string; role: string; permissions?: string[] | null } {
  // Dedicated ERP phone+OTP login (ErpLogin.tsx) — carries real identity +
  // permissions from the login response.
  try {
    const rawErp = localStorage.getItem('erp_user');
    if (rawErp) {
      const u = JSON.parse(rawErp);
      return {
        name: u?.name || 'Staff',
        role: u?.role_name || 'Staff',
        permissions: u?.permissions ?? null,
      };
    }
  } catch { /* fall through to crm_user */ }

  // CRM-hosted /erp embed — staff sign in via the normal CRM login, which has
  // no permission set to filter on, so nav stays unfiltered (current behavior).
  try {
    const raw = localStorage.getItem('crm_user');
    if (!raw) return { name: 'Admin', role: 'School Admin' };
    const u = JSON.parse(raw);
    const data = u?.data?.data ?? u?.data ?? u;
    return {
      name: data?.name || data?.username || 'Admin',
      role: data?.client_id_fk == 9 ? 'YellowSlate Master' : 'School Admin',
    };
  } catch {
    return { name: 'Admin', role: 'School Admin' };
  }
}

// permissions: null (super_admin) or undefined (no data) → show everything.
// `action` defaults to 'view' (every existing nav item) — pass e.g. 'import'
// for an item gated on a different permission action than the module's view.
function canSeeNavItem(permissions: string[] | null | undefined, perm: string | undefined, action: string = 'view') {
  if (!perm || permissions === null || permissions === undefined) return true;
  return permissions.includes(`${perm}.${action}`);
}

function initials(name: string) {
  return name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function ParentAppSidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { school } = useContext(ParentAppContext);
  const user = getUser();

  const visibleSections = NAV_SECTIONS
    .map(section => ({ ...section, items: section.items.filter(item => canSeeNavItem(user.permissions, item.perm, (item as { action?: string }).action)) }))
    .filter(section => section.items.length > 0);

  const isActive = (path: string) => {
    const base = erpBase(); // '' on erp.* host, '/erp' on CRM host
    if (location.pathname === base || location.pathname === `${base}/`) return path === 'dashboard';
    return location.pathname === erpPath(path);
  };

  return (
    <div className={`side${collapsed ? ' collapsed' : ''}`}>
      {/* Collapse toggle — direct child of .side so overflow:visible applies */}
      <button
        className="side-collapse"
        onClick={() => setCollapsed(c => !c)}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed
          ? <ChevronRight size={13} strokeWidth={2.5} />
          : <ChevronLeft  size={13} strokeWidth={2.5} />}
      </button>

      {/* Logo header */}
      <div className="side-head">
        <div className="side-logo">
          <img src="/ys-icon.png" alt="YS" style={{ width: 40, height: 40, objectFit: 'cover' }} />
        </div>
        {!collapsed && (
          <div className="side-head-text">
            <span className="t">School ERP</span>
            <span className="s">Operations Center</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="side-nav">
        {visibleSections.map(section => (
          <div key={section.title} className="nav-section">
            {collapsed
              ? <div className="nav-section-rule" />
              : <div className="nav-section-title">{section.title}</div>}
            {section.items.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={erpPath(item.path)} className={`nav-item${active ? ' active' : ''}`}>
                  <div className="nav-tile">
                    <Icon size={17} strokeWidth={2} />
                  </div>
                  {!collapsed && (
                    <div className="nav-text">
                      <span className="l">{item.label}</span>
                      <span className="d">{item.desc}</span>
                    </div>
                  )}
                  {collapsed && (
                    <div style={{
                      position: 'absolute', left: '100%', marginLeft: 8,
                      background: 'var(--slate)', color: '#fff',
                      fontSize: 12, fontWeight: 700, padding: '5px 10px',
                      borderRadius: 8, whiteSpace: 'nowrap', pointerEvents: 'none',
                      opacity: 0, transition: 'opacity .12s',
                    }} className="nav-tooltip">
                      {item.label}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer — school name + user */}
      <div className="side-foot">
        <div className="side-foot-card">
          <div
            className="avatar"
            style={{ width: 34, height: 34, fontSize: 12, flexShrink: 0, background: 'var(--accent)', color: 'var(--slate)', fontWeight: 800 }}
          >
            {initials(school?.school_name || user.name)}
          </div>
          {!collapsed && (
            <div className="side-foot-text">
              <span className="n">{school?.school_name || user.name}</span>
              <span className="r">{user.name} · {user.role}</span>
            </div>
          )}
          {!collapsed && (
            <Link
              to="/logout"
              title="Logout"
              style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0, transition: 'color .12s' }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.7)')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)')}
            >
              <LogOut size={15} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
